import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import MenuGroup from '@atlaskit/menu/menu-group';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import type { DevDocsBylineConnection } from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';
import { DevDocsBylineConnectionMenuSection } from '@confluence/dev-docs/entry-points/DevDocsBylineConnectionMenuSection.ts';

import {
	BylineConnectionIconStack,
	type BylineConnectionIconStackProps,
} from './BylineConnectionIconStack.tsx';
import { ConnectionsByLineExpandableLinkSection } from './ConnectionsByLineExpandableLinkSection.tsx';
import { ConnectionsByLineMenuFooter } from './footer/ConnectionsByLineMenuFooter.tsx';
import { ConnectionsByLineMenuHeader, HEADER_HEADING_ID } from './ConnectionsByLineMenuHeader.tsx';
import { groupSmartLinksByProduct } from './utils/groupSmartLinksByProduct.ts';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';
import { useTwgAnalyticsEvents } from './useTwgAnalyticsEvents.ts';

const MENU_WIDTH = '320px';
const MAX_VISIBLE_RELATED_CONTENT = 5;
const TWG_MENU_BODY_MAX_HEIGHT = '600px';

const i18n = defineMessages({
	viewMoreContext: {
		id: 'byline-connections.menu.view-more-context',
		defaultMessage: 'View more context',
		description:
			'Label for the button at the end of the connections byline menu that opens the Teamwork Graph preview panel for the current page.',
	},
	relatedContent: {
		id: 'byline-connections.menu.section.related-content',
		defaultMessage: 'Related content ({count})',
		description:
			'Heading of the connections byline menu related content section, followed by the number of Confluence pages and items that Teamwork Graph identifies as related to the current page in parentheses.',
	},
});

/** Shared with the embedded menu, whose Related content heading is the same message. */
export const relatedContentHeadingMessage: MessageDescriptor = i18n.relatedContent;

export type ConnectionGroup = {
	product: string;
	productKey: string;
	items: EnhancedSmartLink[];
};

export type ConnectionItemHandler = (item: EnhancedSmartLink) => void;
export type RefreshLinkUrlHandler = (
	item: EnhancedSmartLink,
) => Promise<EnhancedSmartLink | undefined>;

const styles = cssMap({
	menu: {
		width: MENU_WIDTH,
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.medium'),
	},
	twgContent: {
		paddingBlockEnd: token('space.025'),
		maxHeight: TWG_MENU_BODY_MAX_HEIGHT,
		overflowY: 'auto',
	},
	viewMoreContext: {
		paddingInline: token('space.100'),
		paddingBlock: token('space.150'),
	},
});

export type MenuShownAttributes = {
	linkCount: number;
	productBreakdown: Record<string, number>;
	authorizedLinkCount: number;
	unauthorizedLinkCount: number;
	connectableLinkCount: number;
};

export const getMenuShownAttributes = (groups: ConnectionGroup[] = []): MenuShownAttributes => {
	const attrs: MenuShownAttributes = {
		linkCount: 0,
		productBreakdown: {},
		authorizedLinkCount: 0,
		unauthorizedLinkCount: 0,
		connectableLinkCount: 0,
	};

	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- linear in total link count; groups partition a finite link set
	for (const group of groups) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- linear in total link count; groups partition a finite link set
		for (const item of group.items) {
			attrs.linkCount += 1;
			attrs.productBreakdown[group.productKey] =
				(attrs.productBreakdown[group.productKey] ?? 0) + 1;
			const isAuthorized = item.access === 'granted';
			if (isAuthorized) {
				attrs.authorizedLinkCount += 1;
			} else {
				attrs.unauthorizedLinkCount += 1;
				if ((item.authProviders?.length ?? 0) > 0) {
					attrs.connectableLinkCount += 1;
				}
			}
		}
	}

	return attrs;
};

export const getUnauthenticatedLinkCount = (links: EnhancedSmartLink[] = []): number =>
	links.filter((link) => link.access === 'unauthorized').length;

export type ConnectionsByLineMenuProps = Pick<
	BylineConnectionIconStackProps,
	'additionalIcons' | 'minIcons'
> & {
	showFooter?: boolean;
	links?: EnhancedSmartLink[];
	relatedContentLinks?: EnhancedSmartLink[];
	devDocsConnection?: DevDocsBylineConnection | null;
	contentId?: string;
	refreshLinkUrl: RefreshLinkUrlHandler;
	onNavigate?: () => void;
	onOpenPreviewPanel: () => void;
};

export const ConnectionsByLineMenu = ({
	showFooter = true,
	links,
	additionalIcons,
	minIcons,
	relatedContentLinks,
	devDocsConnection,
	contentId,
	refreshLinkUrl,
	onNavigate,
	onOpenPreviewPanel,
}: ConnectionsByLineMenuProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const totalRelatedContentCount = relatedContentLinks?.length ?? 0;
	const visibleRelatedContent = useMemo(
		() => (relatedContentLinks ?? []).slice(0, MAX_VISIBLE_RELATED_CONTENT),
		[relatedContentLinks],
	);
	// Outgoing links still feed the footer and app icons; only rendered related rows count as viewed links.
	const menuShownAttributes = useMemo(
		() => getMenuShownAttributes(groupSmartLinksByProduct(visibleRelatedContent)),
		[visibleRelatedContent],
	);
	const unauthenticatedLinkCount = useMemo(
		() => getUnauthenticatedLinkCount(visibleRelatedContent),
		[visibleRelatedContent],
	);
	const hasFiredRef = useRef(false);
	const hasFiredUnauthenticatedRef = useRef(false);

	const handleViewMoreContext = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'connectionsBylineViewMoreContext',
				source: 'connectionsBylineMenu',
			},
		}).fire();
		onOpenPreviewPanel();
	}, [createAnalyticsEvent, onOpenPreviewPanel]);

	useEffect(() => {
		if (hasFiredRef.current) {
			return;
		}
		hasFiredRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'menu',
				actionSubjectId: 'connectionsBylineMenu',
				source: 'connectionsBylineMenu',
				attributes: menuShownAttributes,
			},
		}).fire();
	}, [createAnalyticsEvent, menuShownAttributes]);

	useEffect(() => {
		if (hasFiredUnauthenticatedRef.current || unauthenticatedLinkCount === 0) {
			return;
		}
		hasFiredUnauthenticatedRef.current = true;
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'viewed',
				actionSubject: 'unauthenticatedSmartlink',
				actionSubjectId: 'connectionsBylineMenu',
				source: 'connectionsBylineMenu',
				attributes: { unauthenticatedLinkCount },
			},
		}).fire();
	}, [createAnalyticsEvent, unauthenticatedLinkCount]);

	return (
		<Box xcss={styles.menu} role="dialog" aria-labelledby={HEADER_HEADING_ID}>
			<Stack>
				<ConnectionsByLineMenuHeader />
				<Box xcss={styles.twgContent} testId="connections-byline-menu.content">
					{devDocsConnection ? (
						<MenuGroup>
							<DevDocsBylineConnectionMenuSection
								connection={devDocsConnection}
								onNavigate={onNavigate}
							/>
						</MenuGroup>
					) : null}
					{/* Related rows stay compact and static; View more context opens the full preview panel. */}
					<ConnectionsByLineExpandableLinkSection
						heading={formatMessage(i18n.relatedContent, { count: totalRelatedContentCount })}
						links={visibleRelatedContent}
						refreshLinkUrl={refreshLinkUrl}
						linkDirection="incoming"
						onNavigate={onNavigate}
						compact
					/>
					{showFooter ? (
						<ConnectionsByLineMenuFooter links={links ?? []} contentId={contentId} />
					) : null}
					<Box xcss={styles.viewMoreContext}>
						<Button
							appearance="subtle"
							iconBefore={TeamworkGraphIcon}
							iconAfter={ArrowRightIcon}
							shouldFitContainer
							onClick={handleViewMoreContext}
							testId="connections-byline-menu.view-more-context-button"
						>
							<Inline as="span" space="space.100" alignBlock="center">
								<Text weight="regular">{formatMessage(i18n.viewMoreContext)}</Text>
								<BylineConnectionIconStack
									links={links ?? []}
									additionalIcons={additionalIcons}
									minIcons={minIcons}
									spacing="none"
								/>
							</Inline>
						</Button>
					</Box>
				</Box>
			</Stack>
		</Box>
	);
};
